import { useSession } from '@repo/api-client/react';
import { Bell, LogOut, Menu, User2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { ThemeToggle } from '@/features/theme';

interface TopBarProps {
  onOpenDrawer: () => void;
}

export function TopBar({ onOpenDrawer }: TopBarProps) {
  const { user, signOut } = useSession();
  const navigate = useNavigate();

  const initials = composeInitials(user?.firstName, user?.lastName, user?.email);

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b border-line bg-card/80 px-3 backdrop-blur sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
        >
          <Menu className="size-5" />
        </Button>

        <div className="flex-1" />

        <ThemeToggle />

        <NotificationsBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-10 rounded-full p-0"
              aria-label="Account menu"
            >
              <Avatar className="size-9">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="truncate text-sm font-semibold leading-tight">
                  {user?.firstName ?? user?.email ?? 'You'}
                </span>
                <span className="truncate text-[11px] font-normal normal-case tracking-normal text-muted-foreground">
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/settings/account')}>
              <User2 className="size-4" /> Account
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/notifications')}>
              <Bell className="size-4" /> Notifications
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              danger
              onSelect={() => {
                void signOut();
              }}
            >
              <LogOut className="size-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
    </>
  );
}

function composeInitials(
  first?: string | null,
  last?: string | null,
  email?: string | null,
): string {
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return 'IS';
}
